// Oike — recibos internos (docs/servicios/cobros.md): the rules, pure (charges.mts and the tests).
// A receipt is an internal proof of a confirmed payment (R-AAAA-0001); a refund has its own document
// (D-AAAA-0001). Neither is an invoice. The content is frozen when issued (snapshot) and every later
// change stays in the history, never in the issued document. Without the issuer's required data there
// is no document: the payment stays confirmed with its receipt pending.
import { CHARGE, PAYMENT, APPLICATION } from './billingSchema.js';
import { planGroupOf } from './charges.js';
import { chainsOf } from './quoteChains.js';
import { chargeCollected, paymentBalance, cents, money } from './payments.js';

const links = (v) => (Array.isArray(v) ? v.map(x => (typeof x === 'object' ? x?.id : x)).filter(Boolean) : []);
const sel = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v.name : v) || '';
const text = (v, max = 300) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, max);
const EMAIL_RE = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;

/** The issuer's data Ale configures (Billing Settings). The name is always required; she marks the rest. */
export const ISSUER_FIELDS = [
  { key: 'name', label: 'Nombre o razón social', always: true },
  { key: 'taxId', label: 'Identificación (si corresponde)' },
  { key: 'address', label: 'Domicilio' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Teléfono' },
  { key: 'footer', label: 'Texto al pie del recibo' },
];

/**
 * The look of Oike's new commercial documents (quotes and receipts of the central base): only visual elements,
 * kept in Billing Settings with the issuer's data. The defaults are the colors and fonts Ale approved for her identity
 * (the same values as her brand profile, copied here): the documents never read Mi marca, its notes nor its content.
 * Fonts are the ones every PDF reader has (serif and sans), so nothing is embedded or downloaded.
 */
export const DOCUMENT_VISUAL_DEFAULTS = {
  header: 'OIKE',
  colors: { primary: '#294336', background: '#F7F2E9', text: '#432A26', accent: '#CB9E27' },
  fonts: { title: 'Georgia', body: 'Arial' },
};
export const DOCUMENT_FONTS = ['Georgia', 'Arial'];
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
export function cleanVisual(v = {}) {
  const d = DOCUMENT_VISUAL_DEFAULTS;
  const colors = {};
  for (const k of Object.keys(d.colors)) { const c = String(v?.colors?.[k] || d.colors[k]).trim(); if (!HEX_RE.test(c)) throw new Error('invalid_color'); colors[k] = c.toUpperCase(); }
  const fonts = {};
  for (const k of Object.keys(d.fonts)) { const f = String(v?.fonts?.[k] || d.fonts[k]); if (!DOCUMENT_FONTS.includes(f)) throw new Error('invalid_font'); fonts[k] = f; }
  return { header: text(v?.header, 60) || d.header, colors, fonts };
}

/** Cleans what Ale saves: only known fields, trimmed; `required` only among them; the documents' look. */
export function cleanIssuerSettings(input = {}) {
  const issuer = {};
  for (const f of ISSUER_FIELDS) issuer[f.key] = text(input.issuer?.[f.key], f.key === 'footer' ? 600 : 200);
  if (issuer.email && !EMAIL_RE.test(issuer.email)) throw new Error('invalid_issuer_email');
  const required = [...new Set(['name', ...(Array.isArray(input.required) ? input.required : [])])].filter(k => ISSUER_FIELDS.some(f => f.key === k));
  return { issuer, required, visual: cleanVisual(input.visual || {}) };
}
/** The look to use: the saved one, or the defaults (documents saved before the look existed keep theirs). */
export const visualOf = (settings) => { try { return cleanVisual(settings?.visual || {}); } catch { return cleanVisual({}); } };

/** The required issuer data that is missing (never filled in by Oike). */
export function issuerMissing(settings) {
  if (!settings?.issuer) return ['name'];
  const required = settings.required?.length ? settings.required : ['name'];
  return required.filter(k => !String(settings.issuer[k] || '').trim());
}

/** The next free number of a series for a year (R or D), from the numbers already taken. */
export function nextNumber(prefix, year, taken = []) {
  const re = new RegExp(`^${prefix}-${year}-(\\d{4,})$`);
  const max = taken.reduce((m, n) => { const x = re.exec(String(n || '')); return x ? Math.max(m, Number(x[1])) : m; }, 0);
  return `${prefix}-${year}-${String(max + 1).padStart(4, '0')}`;
}

const kindLabel = (f) => (f[CHARGE.installmentKind] === 'installment' ? `cuota ${f[CHARGE.installmentNumber]}/${f[CHARGE.installmentsTotal]}` : f[CHARGE.installmentKind] === 'advance' ? 'anticipo' : f[CHARGE.installmentKind] === 'balance' ? 'saldo' : 'pago completo');

/**
 * The frozen content of a receipt (payment) or refund document, from what is registered now.
 * ctx: { charges, payments, applications, intakes, issuer, names: { [id]: name }, number, issuedAt, originalReceipt }
 */
export function documentSnapshot(movement, ctx) {
  const f = movement.fields || {};
  const refund = sel(f[PAYMENT.entryType]) === 'refund';
  const { charges = [], payments = [], applications = [], intakes = [], issuer, names = {} } = ctx;
  const chains = chainsOf(intakes);
  const versionOf = (quoteId) => { for (const c of chains) { const v = c.versions.find(x => x.id === quoteId); if (v) return v; } return null; };
  const apps = applications.filter(a => sel(a.fields?.[APPLICATION.status]) === 'active' && links(a.fields?.[APPLICATION.payment]).includes(movement.id));
  const lines = apps.map(a => {
    const charge = charges.find(c => c.id === links(a.fields?.[APPLICATION.charge])[0]);
    const cf = charge?.fields || {};
    const quoteId = links(cf[CHARGE.quote])[0];
    const v = quoteId ? versionOf(quoteId) : null;
    const beneficiary = links(cf[CHARGE.beneficiary])[0];
    return {
      concept: cf[CHARGE.concept] || 'Cobro', kind: kindLabel(cf), amount: money(cents(a.fields?.[APPLICATION.amount] || 0)),
      proposal: v ? { name: v.record.fields?.['Name'] || '', version: v.number } : null, beneficiary: beneficiary ? names[beneficiary] || '' : '',
      // What this charge still owes right after this document (the "saldo resultante al emitirlo")
      pendingAfter: charge ? money(Math.max(cents(cf[CHARGE.amount] || 0) - cents(chargeCollected(charge.id, { payments, applications })), 0)) : null,
    };
  });
  const payerId = links(f[sel(f[PAYMENT.payerType]) === 'company' ? PAYMENT.payerAccount : PAYMENT.payerStakeholder])[0];
  const bal = refund ? null : paymentBalance(movement, { payments, applications });
  // What the contratación (each quote covered) still owes right after this document: all its open installments
  const balances = [];
  // By quote, or (without a quote) by its plan; each group with all its open installments
  // The same plan as the screens (charges.js › planGroupOf): a moved rest counts in the balance of its plan
  const groupOf = (c) => (c ? planGroupOf(c, charges) : '');
  const covered = [...new Set(apps.map(a => groupOf(charges.find(c => c.id === links(a.fields?.[APPLICATION.charge])[0]))).filter(g => g && !g.endsWith(':undefined')))];
  for (const g of covered) {
    const open = charges.filter(c => groupOf(c) === g && sel(c.fields?.[CHARGE.status]) === 'open');
    if (!open.length) continue;
    const owes = (c) => Math.max(cents(c.fields?.[CHARGE.amount] || 0) - cents(chargeCollected(c.id, { payments, applications })), 0);
    const quoteId = g.startsWith('q:') ? g.slice(2) : '';
    const v = quoteId ? versionOf(quoteId) : null;
    const nextDue = open.filter(c => owes(c) > 0).map(c => c.fields?.[CHARGE.dueOn]).filter(Boolean).sort()[0] || '';
    balances.push({ proposal: v ? v.record.fields?.['Name'] || '' : '', concept: open[0].fields?.[CHARGE.concept] || '', pending: money(open.reduce((sum, c) => sum + owes(c), 0)), nextDue });
  }
  return {
    // v2: the designed document (its look frozen with it); v1 receipts keep rendering as they were issued
    v: ctx.visual ? 2 : 1, ...(ctx.visual ? { visual: ctx.visual, balances, layout: 2 } : {}), kind: refund ? 'refund' : 'receipt', number: ctx.number, issuedAt: ctx.issuedAt,
    issuer: { ...issuer },
    payer: { type: sel(f[PAYMENT.payerType]), name: names[payerId] || '' },
    receivedOn: f[PAYMENT.receivedOn], amount: money(cents(f[PAYMENT.amount] || 0)), currency: f[PAYMENT.currency],
    method: f[PAYMENT.method] || '', reference: f[PAYMENT.reference] || '',
    lines, unapplied: bal ? bal.unapplied : null,
    originalReceipt: refund ? ctx.originalReceipt || '' : undefined,
    note: refund ? 'Comprobante interno de devolución. No es una factura ni un comprobante fiscal.' : 'Recibo interno de pago. No es una factura ni un comprobante fiscal.',
  };
}

const fmt = (n, cur) => `${cur} ${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The lines of the PDF, from the frozen snapshot only. */
export function documentLines(s) {
  const refund = s.kind === 'refund';
  const L = [];
  L.push({ text: refund ? 'Comprobante de devolución' : 'Recibo de pago', size: 18, bold: true });
  L.push({ text: `N.º ${s.number} · emitido el ${String(s.issuedAt || '').slice(0, 10)}`, size: 10 });
  L.push({ text: 'Emisor', size: 11, bold: true, gap: 10 });
  for (const f of ISSUER_FIELDS) if (f.key !== 'footer' && s.issuer?.[f.key]) L.push({ text: `${f.key === 'name' ? '' : `${f.label.replace(/ \(.*\)/, '')}: `}${s.issuer[f.key]}`, size: 10 });
  L.push({ text: refund ? 'Devuelto a' : 'Recibido de', size: 11, bold: true, gap: 10 });
  L.push({ text: `${s.payer?.name || '—'} (${s.payer?.type === 'company' ? 'empresa' : 'persona'})`, size: 10 });
  const beneficiaries = [...new Set((s.lines || []).map(l => l.beneficiary).filter(Boolean))];
  if (beneficiaries.length) L.push({ text: `Beneficiaria: ${beneficiaries.join(', ')}`, size: 10 });
  L.push({ text: refund ? 'Devolución' : 'Pago', size: 11, bold: true, gap: 10 });
  L.push({ text: `Fecha: ${s.receivedOn}`, size: 10 });
  L.push({ text: `Importe: ${fmt(s.amount, s.currency)}`, size: 12, bold: true });
  if (s.method) L.push({ text: `Medio: ${s.method}`, size: 10 });
  if (s.reference) L.push({ text: `Referencia: ${s.reference}`, size: 10 });
  if (refund && s.originalReceipt) L.push({ text: `Corresponde al pago del recibo ${s.originalReceipt}`, size: 10 });
  // The amount once, with its split right below it; then how it was spread, grouped by proposal (its name once),
  // and what each charge still owes right after this document
  const appliedTotal = (s.lines || []).reduce((sum, l) => sum + Math.round(Number(l.amount || 0) * 100), 0) / 100;
  if ((s.lines || []).length || (s.unapplied || 0) > 0) L.push({ text: `${refund ? 'Tomado de cobros' : 'Aplicado a cobros'}: ${fmt(appliedTotal, s.currency)}${!refund && (s.unapplied || 0) > 0 ? ` · sin aplicar: ${fmt(s.unapplied, s.currency)}` : ''}`, size: 10 });
  L.push({ text: refund ? 'De dónde sale' : 'Cómo se aplicó', size: 11, bold: true, gap: 10 });
  if (!(s.lines || []).length) L.push({ text: refund ? 'Del dinero recibido sin aplicar.' : 'Todavía sin aplicar a un cobro.', size: 10 });
  const groups = [];
  for (const l of s.lines || []) {
    const key = l.proposal ? `${l.proposal.name}|${l.proposal.version}` : 'direct';
    let g = groups.find(x => x.key === key);
    if (!g) groups.push(g = { key, proposal: l.proposal, lines: [] });
    g.lines.push(l);
  }
  let n = 0;
  for (const g of groups) {
    L.push({ text: g.proposal ? `Propuesta «${g.proposal.name}», versión ${g.proposal.version}` : 'Cobros directos', size: 10, bold: true, gap: 6 });
    for (const l of g.lines) {
      n += 1;
      L.push({ text: `${n}. ${l.kind} · ${l.concept}${l.beneficiary ? ` · beneficiaria: ${l.beneficiary}` : ''}`, size: 10, indent: 8 });
      L.push({ text: `${refund ? 'Devuelto' : 'Aplicado'}: ${fmt(l.amount, s.currency)}${l.pendingAfter !== null && l.pendingAfter !== undefined ? ` · saldo de esa cuota al emitir: ${fmt(l.pendingAfter, s.currency)}` : ''}`, size: 10, indent: 22 });
    }
  }
  if ((s.lines || []).length) L.push({ text: `Total ${refund ? 'tomado de cobros' : 'aplicado'}: ${fmt(appliedTotal, s.currency)}${!refund && (s.unapplied || 0) > 0 ? ` · sin aplicar: ${fmt(s.unapplied, s.currency)}` : ''}`, size: 10, bold: true, gap: 6 });
  L.push({ text: s.note, size: 9, gap: 16 });
  if (s.issuer?.footer) L.push({ text: s.issuer.footer, size: 9 });
  return L;
}

/** The sends of a document, from the movement's log (`[envío:<key>:<state>…]`): only "enviado" when Gmail confirmed it. */
export function sendsOf(log = '') {
  const out = new Map();
  for (const line of String(log).split('\n')) {
    const m = /^\[envío:([A-Za-z0-9-]+):(intentando|enviado|falló)(?::([^\]]*))?\]\s*(.*)$/.exec(line);
    if (!m) continue;
    const [, key, state, gmailId, rest] = m;
    const prev = out.get(key);
    // The log is newest first: the first line seen for a key is its latest state
    if (!prev) out.set(key, { key, state, gmailId: gmailId || '', detail: rest });
  }
  return [...out.values()];
}
export const validEmail = (e) => EMAIL_RE.test(String(e || '').trim());

// ── Corrections of an issued receipt (docs/servicios/cobros.md › «Corrección de un recibo») ──
// The issued receipt is never changed. A correction is another frozen document of the SAME payment (no other income,
// nothing applied or confirmed again), numbered after it (R-2026-0004-C1, -C2…), with the figures as they are now
// (what each line still owes and the plan's total balance with its next due date), the reason, who and when.
// Kept in Payments › Receipt Corrections JSON; the original stays downloadable. Nothing is sent by itself.
export function correctionsOf(f = {}) {
  try { const v = JSON.parse(f[PAYMENT.receiptCorrections] || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}
const figures = (s) => JSON.stringify([(s?.lines || []).map(l => [l.concept, l.kind, l.amount, l.pendingAfter]), (s?.balances || []).map(b => [b.pending, b.nextDue]), s?.unapplied ?? null]);
/** The figures that changed against the latest version (the receipt or its latest correction); none → no correction. */
export function correctionNeeded(original, corrections = [], fresh) {
  const latest = corrections.length ? corrections[corrections.length - 1].snapshot : original;
  return figures(latest) !== figures(fresh);
}
export function correctionEntry(original, corrections, fresh, { reason, by, key, now = new Date() }) {
  const r = String(reason || '').replace(/\s+/g, ' ').trim().slice(0, 500);
  if (r.length < 10) throw new Error('reason_required');
  const number = `${original.number}-C${corrections.length + 1}`;
  const issuedAt = now.toISOString();
  return {
    number, key, issuedAt, reason: r, by: String(by || '').slice(0, 200),
    snapshot: { ...fresh, number, issuedAt, correctionOf: original.number, correctionReason: r,
      note: `Corrige el recibo ${original.number}: el importe recibido no cambia; se corrigen los saldos informados. ${fresh.note || ''}`.trim() },
  };
}
